import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Box from '@mui/material/Box';
import { faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import Button from '@mui/material/Button';

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
  children: React.ReactElement ;
}

function ElevationScroll(props: Props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 20,
    target: window ? window() : undefined,
  });

  return React.cloneElement(children, {
    elevation: 0,
    sx: {
      backgroundColor: trigger ? "rgba(250, 248, 245, 0.94)" : "rgba(250, 248, 245, 0.82)",
      backdropFilter: "blur(20px)",
      borderBottom: trigger ? "1px solid rgba(158, 119, 54, 0.18)" : "1px solid rgba(45, 33, 26, 0.05)",
      transition: "all 0.3s ease",
      boxShadow: trigger ? "0 4px 24px rgba(45, 33, 26, 0.06)" : "none",
    }
  });
}

interface Props2 {
    window?: () => Window;
    children: React.ReactElement | React.ReactElement[];
  }

export default function ElevateAppBar(props: Props2) {
  return (
    <React.Fragment>
      <ElevationScroll {...props}>
        <AppBar position="fixed" color="inherit">
          <Toolbar sx={{ justifyContent: "space-between", py: 1, px: { xs: 2, md: 5 } }}>
            <Box display="flex" alignItems="center">
              <a href="/" style={{ display: "block" }}>
                <img
                  src="/images/logo-v2.svg"
                  alt="Hua Hao Yue Yuan | Centro de masajes"
                  style={{ height: "32px", display: "block" }}
                />
              </a>
            </Box>

            {/* Navigation links centered on desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Button href="/" sx={{ color: "#1C1612", fontWeight: 500, fontSize: "0.88rem", px: 2, py: 0.8, borderRadius: "20px", textTransform: "none", transition: "all 0.2s", "&:hover": { backgroundColor: "rgba(158, 119, 54, 0.08)", color: "#9E7736" } }}>
                Inicio
              </Button>
              <Button href="#tratamientos" sx={{ color: "#68594F", fontWeight: 500, fontSize: "0.88rem", px: 2, py: 0.8, borderRadius: "20px", textTransform: "none", transition: "all 0.2s", "&:hover": { backgroundColor: "rgba(158, 119, 54, 0.08)", color: "#9E7736" } }}>
                Tratamientos
              </Button>
              <Button href="#instalaciones" sx={{ color: "#68594F", fontWeight: 500, fontSize: "0.88rem", px: 2, py: 0.8, borderRadius: "20px", textTransform: "none", transition: "all 0.2s", "&:hover": { backgroundColor: "rgba(158, 119, 54, 0.08)", color: "#9E7736" } }}>
                Instalaciones
              </Button>
              <Button href="#contacto" sx={{ color: "#68594F", fontWeight: 500, fontSize: "0.88rem", px: 2, py: 0.8, borderRadius: "20px", textTransform: "none", transition: "all 0.2s", "&:hover": { backgroundColor: "rgba(158, 119, 54, 0.08)", color: "#9E7736" } }}>
                Contacto
              </Button>
            </Box>

            {/* Action buttons (Desktop only) */}
            <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1.5 }}>
              <Button
                href="tel:+34605245681"
                variant="text"
                size="small"
                startIcon={<FontAwesomeIcon size="xs" icon={faPhone} />}
                sx={{
                  color: "#1C1612",
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  textTransform: "none",
                  borderRadius: "20px",
                  px: 1.8,
                  py: 0.8,
                  border: "1px solid rgba(45, 33, 26, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(158, 119, 54, 0.08)",
                    borderColor: "#9E7736",
                    color: "#9E7736",
                  },
                }}
              >
                605 245 681
              </Button>

              <Button
                href="https://wa.link/kffk9v"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="small"
                startIcon={<FontAwesomeIcon icon={faWhatsapp} />}
                sx={{
                  background: "linear-gradient(135deg, #DFBA73 0%, #C5A059 50%, #9E7736 100%)",
                  color: "#1C1612",
                  borderRadius: "20px",
                  fontWeight: 700,
                  fontSize: "0.84rem",
                  px: 2.5,
                  py: 0.8,
                  textTransform: "none",
                  boxShadow: "0 4px 14px rgba(158, 119, 54, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #E6C787 0%, #B89248 50%, #8E6A26 100%)",
                    boxShadow: "0 6px 18px rgba(158, 119, 54, 0.35)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                WhatsApp
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      </ElevationScroll>
      <Toolbar />
      {props.children}

      {/* Fixed Bottom Action Bar (Mobile Only) */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          backgroundColor: "rgba(250, 248, 245, 0.94)",
          backdropFilter: "blur(20px)",
          borderTop: "1px solid rgba(158, 119, 54, 0.2)",
          boxShadow: "0 -4px 20px rgba(45, 33, 26, 0.08)",
          py: 1.2,
          px: 2,
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Button
          href="tel:+34605245681"
          variant="outlined"
          fullWidth
          startIcon={<FontAwesomeIcon icon={faPhone} />}
          sx={{
            flex: 1,
            color: "#1C1612",
            backgroundColor: "#FFFFFF",
            borderColor: "rgba(158, 119, 54, 0.35)",
            borderRadius: "24px",
            fontWeight: 700,
            fontSize: "0.85rem",
            py: 1.1,
            textTransform: "none",
            boxShadow: "0 2px 8px rgba(45, 33, 26, 0.04)",
            "&:hover": {
              backgroundColor: "rgba(212, 175, 55, 0.08)",
              borderColor: "#9E7736",
            },
          }}
        >
          Llamar
        </Button>

        <Button
          href="https://wa.link/kffk9v"
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          fullWidth
          startIcon={<FontAwesomeIcon icon={faWhatsapp} />}
          sx={{
            flex: 1.4,
            background: "linear-gradient(135deg, #DFBA73 0%, #C5A059 50%, #9E7736 100%)",
            color: "#1C1612",
            borderRadius: "24px",
            fontWeight: 700,
            fontSize: "0.85rem",
            py: 1.1,
            textTransform: "none",
            boxShadow: "0 4px 14px rgba(158, 119, 54, 0.28)",
            "&:hover": {
              background: "linear-gradient(135deg, #E6C787 0%, #B89248 50%, #8E6A26 100%)",
            },
          }}
        >
          WhatsApp
        </Button>
      </Box>
    </React.Fragment>
  );
}